// src/context/AuthContext.jsx
import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import authService from '../services/auth.service';
import api from '../services/api';
import { ROLE_HIERARCHY } from '../constants/roles';
import socketService from '../services/socket.service';

const AuthContext = createContext(null);

const AUTH_USER_KEY = 'auth_user';
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const storedUser = localStorage.getItem(AUTH_USER_KEY);
        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
     
        
        if (storedUser && accessToken) {
          try {
            // Try to get fresh profile data
            const response = await api.get('/auth/me');
            
            if (response.data.success && response.data.data) {
              const profileData = response.data.data;
              
              const userData = {
                id: profileData.id,
                username: profileData.username,
                email: profileData.email,
                firstName: profileData.firstName,
                lastName: profileData.lastName,
                name: profileData.fullName || `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || profileData.username,
                phone: profileData.phone,
                avatar: profileData.avatar,
                employeeCode: profileData.employeeCode,
                status: profileData.status,
                role: profileData.role?.name,
                roleLevel: profileData.role?.level,
                roleDisplayName: profileData.role?.displayName,
                roleShortCode: profileData.role?.shortCode,
                companyId: profileData.company?.id,
                companyName: profileData.company?.name,
                reportingTo: profileData.reportingTo,
                reportingManager: profileData.reportingManager,
              };
              
              setUser(userData);
              localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
              socketService.connect();
            }
          } catch (err) {
            console.error('Error fetching profile, using stored user:', err);
            // If API call fails, use stored user data
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            socketService.connect(); 
          }
        }
      } catch (err) {
        console.error('Error loading user:', err);
        clearAuthData();
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  }, []);

  const clearAuthData = () => {
    localStorage.removeItem(AUTH_USER_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setUser(null);
  };

const login = useCallback(async (identifier, password) => {
  setError(null);
  setLoading(true);
  
  try {
    const credentials = { email: identifier, password };
    const response = await authService.login(credentials);

    if (response.success && response.data) {
      const { user: userData, tokens } = response.data;
      
      // ✅ Store tokens FIRST
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      
      // ✅ THEN connect socket (token now available in localStorage)
      socketService.connect();

      const userObj = {
        id: userData.id,
        username: userData.username,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        name: userData.fullName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.username,
        phone: userData.phone,
        avatar: userData.avatar,
        employeeCode: userData.employeeCode,
        status: userData.status,
        role: userData.role?.name,
        roleLevel: userData.role?.level,
        roleDisplayName: userData.role?.displayName,
        roleShortCode: userData.role?.shortCode,
        companyId: userData.company?.id,
        companyName: userData.company?.name,
        reportingTo: userData.reportingTo,
        reportingManager: userData.reportingManager,
      };
      
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userObj));
      setUser(userObj);
      
      return { success: true, user: userObj };
    } else {
      throw new Error(response.message || 'Login failed');
    }
  } catch (err) {
    const errorMessage = err.response?.data?.message || err.message || 'Login failed';
    setError(errorMessage);
    return { success: false, error: errorMessage };
  } finally {
    setLoading(false);
  }
}, []);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (refreshToken) {
         socketService.disconnect();
        await authService.logout(refreshToken);
      }
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      clearAuthData();
    }
  }, []);

   const forgotPassword = useCallback(async (email) => {
  try {
    setLoading(true);
    const response = await authService.forgotPassword(email);
    // Yahan aap toast notification dikha sakte hain
    console.log('Reset link sent:', response.message);
    return response;
  } catch (err) {
    console.error('Forgot password error:', err);
    throw err;
  } finally {
    setLoading(false);
  }
}, []);
  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return null;
      const updated = { ...prev, ...updates };
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const hasRole = useCallback((role) => user?.role === role, [user]);

  const hasAnyRole = useCallback((roles) => {
    if (!user?.role || !roles?.length) return false;
    return roles.includes(user.role);
  }, [user]);

  const hasMinimumRole = useCallback((minimumRole) => {
    if (!user?.role) return false;
    const userLevel = ROLE_HIERARCHY[user.role] || 0;
    const requiredLevel = ROLE_HIERARCHY[minimumRole] || 0;
    return userLevel >= requiredLevel;
  }, [user]);

  const canAccessMenuItem = useCallback((menuItem) => {
    if (!menuItem.roles?.length) return true;
    return hasAnyRole(menuItem.roles);
  }, [hasAnyRole]);

 const isSuperAdmin = useMemo(() =>
  ['SUPER_ADMIN', 'OFFICE_ADMIN'].includes(user?.role),
[user]);

const isAdmin = useMemo(() =>
  ['SUPER_ADMIN', 'OFFICE_ADMIN', 'ADMIN'].includes(user?.role), 
[user]);

  const isAuthenticated = useMemo(() => !!user, [user]);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    login,
    logout,
    updateUser,
    hasRole,
    hasAnyRole,
    hasMinimumRole,
    canAccessMenuItem,
    isSuperAdmin,
    isAdmin,
    isAuthenticated,
    clearError: () => setError(null),
  }), [user, loading, error, login, logout, updateUser, hasRole, hasAnyRole, hasMinimumRole, canAccessMenuItem, isSuperAdmin, isAdmin, isAuthenticated]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;